import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Timer, TrendingUp,
  Search, Settings2, RotateCw,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";
import Shimmer from "../Common/Shimmer";
import CallStatusToast from "../Telephony/CallStatusToast";
import useClickToCall from "../../hooks/useClickToCall";
import {
  StatsCard, StatsGrid, EmptyState, AppButton,
  FilterPanel, FilterField, Input, NativeSelect, FilterRow, FilterButton, ActiveFilterChips,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../ui";
import "./calls.scss";

/**
 * Calls — the call log, the follow-up queue, and per-counsellor performance.
 *
 * Density is tuned for the 15.6" @150% admin viewport (~1280×720 effective):
 * vertical space is the constraint, so the KPI strip is one short row and the
 * table carries the page.
 */

const RANGE_PRESETS = [
  { key: "7d", label: "7d", days: 7 },
  { key: "30d", label: "30d", days: 30 },
  { key: "90d", label: "90d", days: 90 },
];

// The shared StatusBadge's modifiers are enquiry-pipeline words (enquired,
// dropped, placed…), so call outcomes get their own chip rather than being
// shoehorned into a vocabulary that means something else.
const STATUS_TONE = {
  completed: "ok",
  "in-progress": "live",
  ringing: "live",
  queued: "idle",
  "no-answer": "warn",
  busy: "warn",
  missed: "warn",
  failed: "bad",
  canceled: "idle",
};

const STATUS_LABEL = {
  completed: "Connected",
  "in-progress": "In progress",
  ringing: "Ringing",
  queued: "Dialling",
  "no-answer": "No answer",
  busy: "Busy",
  missed: "Missed",
  failed: "Failed",
  canceled: "Cancelled",
};

function fmtDuration(sec) {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtWhen(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function isoDaysAgo(days) {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

const EMPTY_FILTERS = { search: "", direction: "", status: "", from: "", to: "", callMode: "" };

const CALL_MODE_LABEL = { phone: "Phone calling", webrtc: "Browser calling" };

export default function CallsPage() {
  const navigate = useNavigate();

  // Missed calls are fetched once by EnquiryWorkspace, which needs the count for
  // the tab badge anyway — taking it from there rather than re-requesting it
  // means opening this tab costs nothing extra. Falls back to local state when
  // rendered outside the workspace.
  const outlet = useOutletContext() || {};
  const missed = outlet.missed ?? [];
  const loadingMissed = outlet.missedLoading ?? false;

  const [tab, setTab] = useState("log");
  const [preset, setPreset] = useState("30d");

  const [calls, setCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [refreshKey, setRefreshKey] = useState(0);

  // One refresh drives both stores. Calling someone back has to clear them from
  // the callback queue *and* the tab badge, and refreshing only the list would
  // leave the badge claiming work that is already done.
  const reloadMissed = outlet.reloadMissed;
  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    reloadMissed?.();
  }, [reloadMissed]);

  const call = useClickToCall({ onLogged: refresh });

  const range = useMemo(() => {
    if (filters.from || filters.to) return { from: filters.from, to: filters.to };
    const days = RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 30;
    return { from: isoDaysAgo(days), to: "" };
  }, [preset, filters.from, filters.to]);

  const clearField = (key) => setFilters((f) => ({ ...f, [key]: "" }));
  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const activeFilters = [
    filters.direction && {
      key: "direction",
      label: `Direction: ${filters.direction === "inbound" ? "Incoming" : "Outgoing"}`,
      onRemove: () => clearField("direction"),
    },
    filters.status && {
      key: "status",
      label: `Status: ${STATUS_LABEL[filters.status] || filters.status}`,
      onRemove: () => clearField("status"),
    },
    filters.callMode && {
      key: "callMode",
      label: CALL_MODE_LABEL[filters.callMode] || filters.callMode,
      onRemove: () => clearField("callMode"),
    },
    filters.from && { key: "from", label: `From: ${filters.from}`, onRemove: () => clearField("from") },
    filters.to && { key: "to", label: `To: ${filters.to}`, onRemove: () => clearField("to") },
  ].filter(Boolean);

  const activeFilterCount = activeFilters.length;

  // ── Log + analytics ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = {
      page,
      limit: 25,
      ...(filters.direction && { direction: filters.direction }),
      ...(filters.status && { status: filters.status }),
      ...(filters.callMode && { callMode: filters.callMode }),
      ...(filters.search && { search: filters.search }),
      ...(range.from && { from: range.from }),
      ...(range.to && { to: range.to }),
    };

    Promise.all([
      apiClient.get("/telephony/calls", { params }),
      apiClient.get("/telephony/calls/analytics", {
        params: { ...(range.from && { from: range.from }), ...(range.to && { to: range.to }) },
      }),
    ])
      .then(([logRes, statsRes]) => {
        if (cancelled) return;
        setCalls(logRes.data.calls || []);
        setTotal(logRes.data.total || 0);
        setAnalytics(statsRes.data);
      })
      .catch(() => { if (!cancelled) { setCalls([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [page, filters.direction, filters.status, filters.callMode, filters.search, range.from, range.to, refreshKey]);

  // Filters change the result set, so stale pagination would show an empty page.
  useEffect(() => { setPage(1); }, [filters.direction, filters.status, filters.callMode, filters.search, range.from, range.to]);

  const summary = analytics?.summary;

  const renderNumberCell = (c) => (
    <span className="cl-cell-number">
      {c.direction === "inbound"
        ? <PhoneIncoming size={13} className="cl-dir cl-dir--in" />
        : <PhoneOutgoing size={13} className="cl-dir cl-dir--out" />}
      <span>
        {c.enquiryId?.fullName
          ? <button type="button" className="cl-link" onClick={() => navigate(`/enquiries/${c.enquiryId._id}`)}>
              {c.enquiryId.fullName}
            </button>
          : c.customerNumber}
      </span>
    </span>
  );

  const logColumns = [
    { key: "when", label: "When", className: "cl-cell-when", render: (c) => fmtWhen(c.createdAt) },
    { key: "who", label: "Contact", render: renderNumberCell },
    { key: "number", label: "Number", className: "cl-cell-num", render: (c) => c.customerNumber || "—" },
    { key: "agent", label: "Counsellor", render: (c) => c.agentName || "—" },
    {
      key: "status",
      label: "Outcome",
      render: (c) => (
        <span className={`cl-outcome cl-outcome--${STATUS_TONE[c.status] || "idle"}`}>
          {STATUS_LABEL[c.status] || c.status}
        </span>
      ),
    },
    {
      key: "talk",
      label: "Talk time",
      className: "cl-cell-talk",
      render: (c) => fmtDuration(c.talkTimeSec),
    },
  ];

  const missedColumns = [
    { key: "when", label: "Missed at", className: "cl-cell-when", render: (c) => fmtWhen(c.createdAt) },
    { key: "who", label: "Contact", render: renderNumberCell },
    { key: "number", label: "Number", className: "cl-cell-num", render: (c) => c.customerNumber || "—" },
    { key: "owner", label: "Assigned to", render: (c) => c.agentName || "Unassigned" },
    {
      key: "action",
      label: "",
      className: "cl-cell-action",
      render: (c) => (
        call.enabled && (
          <AppButton
            size="sm"
            variant="secondary"
            disabled={call.calling}
            onClick={() => call.placeCall({
              ...(c.enquiryId?._id ? { enquiryId: c.enquiryId._id } : { toNumber: c.customerNumber }),
            })}
          >
            <Phone size={13} /> Call back
          </AppButton>
        )
      ),
    },
  ];

  return (
    <div className="calls-page">
      {/* The page header and the Enquiries/Calls tab rail belong to
          EnquiryWorkspace, the layout route above this one. Refresh and Calling
          settings moved down here onto the range row, which had spare width —
          so the whole header row is saved rather than merely relocated. */}

      {/* ── KPI strip ── */}
      <div className="cl-overview">
        <div className="cl-overview__head">
          <span className="cl-overview__title">Call activity</span>
          <div className="cl-overview__tools">
            <div className="cl-range" role="group" aria-label="Date range">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`cl-range__btn ${preset === p.key && !filters.from && !filters.to ? "is-active" : ""}`}
                  onClick={() => { setFilters((f) => ({ ...f, from: "", to: "" })); setPreset(p.key); }}
                >
                  {p.label}
                </button>
              ))}
              {(filters.from || filters.to) && (
                <span className="cl-range__btn is-active is-custom" title="Custom range from filters">Custom</span>
              )}
            </div>
            <AppButton variant="ghost" size="sm" onClick={refresh} title="Refresh">
              <RotateCw size={14} />
            </AppButton>
            <AppButton variant="secondary" size="sm" onClick={() => navigate("/settings?tab=telephony")}>
              <Settings2 size={14} /> Calling settings
            </AppButton>
          </div>
        </div>

        <StatsGrid className="cl-kpis">
          {loading && !summary ? (
            <Shimmer height={78} count={5} />
          ) : (
            <>
              <StatsCard title="TOTAL CALLS" value={summary?.total ?? 0} icon={Phone} iconColor="blue" />
              <StatsCard title="CONNECTED" value={summary?.connected ?? 0} icon={TrendingUp} iconColor="emerald" />
              <StatsCard
                title="CONNECT RATE"
                value={`${summary?.connectRate ?? 0}%`}
                icon={TrendingUp}
                iconColor="cyan"
              />
              <StatsCard
                title="TALK TIME"
                value={fmtDuration(summary?.totalTalkTimeSec)}
                icon={Timer}
                iconColor="violet"
              />
              <StatsCard
                title="MISSED"
                value={summary?.missedInbound ?? 0}
                icon={PhoneMissed}
                iconColor="rose"
              />
            </>
          )}
        </StatsGrid>
      </div>

      <Tabs value={tab} onChange={setTab}>
        <TabsList>
          <TabsTrigger value="log">Call log</TabsTrigger>
          <TabsTrigger value="missed">
            Needs callback
            {missed.length > 0 && <span className="cl-tab-count">{missed.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="agents">By counsellor</TabsTrigger>
        </TabsList>

        {/* ── Call log ── */}
        <TabsContent value="log">
          <div className="cl-toolbar">
            <div className="cl-search">
              <Search size={16} className="cl-search__icon" />
              <input
                type="text"
                className="cl-search__input"
                placeholder="Search by number…"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>
            <FilterButton count={activeFilterCount} onClick={() => setShowFilters(true)} />
          </div>

          <ActiveFilterChips items={activeFilters} onClearAll={resetFilters} />

          {loading ? (
            <Shimmer height={52} count={6} />
          ) : calls.length === 0 ? (
            <EmptyState
              icon={Phone}
              title="No calls yet"
              description={
                activeFilterCount > 0
                  ? "No calls match these filters. Try widening the date range."
                  : "Calls placed from an enquiry, and calls received on your number, will appear here."
              }
            />
          ) : (
            <DataTable
              data={calls}
              columns={logColumns}
              pagination={{ page, pageSize: 25, total, onPageChange: setPage }}
            />
          )}
        </TabsContent>

        {/* ── Needs callback ── */}
        <TabsContent value="missed">
          {loadingMissed ? (
            <Shimmer height={52} count={4} />
          ) : missed.length === 0 ? (
            <EmptyState
              icon={PhoneMissed}
              title="Nothing waiting"
              description="Every missed call has been returned. This list clears itself when you call someone back."
            />
          ) : (
            <>
              <p className="cl-note">
                Incoming calls nobody got through on, and nobody has rung back since. Calling back from
                here clears the entry automatically.
              </p>
              <DataTable data={missed} columns={missedColumns} />
            </>
          )}
        </TabsContent>

        {/* ── By counsellor ── */}
        <TabsContent value="agents">
          {/* Phone leg vs browser leg, shown only once both exist. Rolling
              browser calling out to a team is a decision someone has to justify,
              and "does it connect as often, and do the calls last as long" is the
              only honest way to answer it — but on a phone-only institute this
              strip is a row of noise, so it stays hidden until there is a
              comparison to make. */}
          {(analytics?.byMode?.length ?? 0) > 1 && (
            <div className="cl-modes">
              {analytics.byMode.map((m) => (
                <div className="cl-mode" key={m.callMode}>
                  <span className="cl-mode__label">
                    {m.callMode === "webrtc" ? "Browser calling" : "Phone calling"}
                  </span>
                  <div className="cl-mode__stats">
                    <span><strong>{m.calls}</strong> calls</span>
                    <span><strong>{m.connectRate}%</strong> connected</span>
                    <span><strong>{fmtDuration(m.avgTalkTimeSec)}</strong> avg</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <Shimmer height={52} count={4} />
          ) : !analytics?.byAgent?.length ? (
            <EmptyState
              icon={Phone}
              title="No counsellor activity"
              description="Once your team starts calling from the CRM, their totals appear here."
            />
          ) : (
            <DataTable
              data={analytics.byAgent}
              columns={[
                { key: "name", label: "Counsellor", render: (a) => a.agentName },
                { key: "calls", label: "Calls", render: (a) => a.calls },
                { key: "connected", label: "Connected", render: (a) => a.connected },
                {
                  key: "rate",
                  label: "Connect rate",
                  render: (a) => (
                    <span className="cl-rate">
                      <span className="cl-rate__bar">
                        <span className="cl-rate__fill" style={{ width: `${a.connectRate}%` }} />
                      </span>
                      {a.connectRate}%
                    </span>
                  ),
                },
                { key: "talk", label: "Talk time", className: "cl-cell-talk", render: (a) => fmtDuration(a.talkTimeSec) },
                { key: "avg", label: "Avg / call", className: "cl-cell-talk", render: (a) => fmtDuration(a.avgTalkTimeSec) },
              ]}
            />
          )}
        </TabsContent>
      </Tabs>

      <FilterPanel
        show={showFilters}
        onHide={() => setShowFilters(false)}
        subtitle="Narrow the call log by direction, outcome and date."
        onReset={resetFilters}
        appliedCount={activeFilterCount}
      >
        <FilterField label="Direction">
          <NativeSelect size="lg"
            value={filters.direction}
            onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value }))}
          >
            <option value="">All calls</option>
            <option value="outbound">Outgoing</option>
            <option value="inbound">Incoming</option>
          </NativeSelect>
        </FilterField>

        <FilterField label="Outcome">
          <NativeSelect size="lg"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">Any outcome</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </NativeSelect>
        </FilterField>

        {/* Same rule as the comparison strip: offered only once both modes have
            calls. On a phone-only institute this filter can only ever return
            "everything" or "nothing", which is a control that wastes a click. */}
        {(analytics?.byMode?.length ?? 0) > 1 && (
          <FilterField label="Placed from">
            <NativeSelect size="lg"
              value={filters.callMode}
              onChange={(e) => setFilters((f) => ({ ...f, callMode: e.target.value }))}
            >
              <option value="">Either</option>
              <option value="phone">{CALL_MODE_LABEL.phone}</option>
              <option value="webrtc">{CALL_MODE_LABEL.webrtc}</option>
            </NativeSelect>
          </FilterField>
        )}

        <FilterRow>
          <FilterField label="From">
            <Input size="lg"
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            />
          </FilterField>
          <FilterField label="To">
            <Input size="lg"
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            />
          </FilterField>
        </FilterRow>
      </FilterPanel>

      <CallStatusToast call={call.activeCall} error={call.error} onDismiss={call.dismiss} />
    </div>
  );
}
